import { Decimal, toDecimal } from './decimal.js';
import { money, type Money } from './money.js';
import { FX_BASE_CURRENCY, type Currency } from '../types/enums.js';
import type { FxRate, IsoDate } from '../types/domain.js';

const ONE = new Decimal(1);

export class FxRateUnavailableError extends Error {
  constructor(
    readonly currency: Currency,
    readonly date: IsoDate,
  ) {
    super(`No EUR/${currency} rate on or before ${date}`);
    this.name = 'FxRateUnavailableError';
  }
}

/** Ascending dates with their rate, kept parallel so lookup is a binary search. */
interface RateSeries {
  dates: IsoDate[];
  rates: Decimal[];
}

/**
 * The whole FX history, indexed for repeated lookups.
 *
 * The client loads this once and re-runs every conversion locally when the
 * display currency changes, so switching currency never hits the server.
 */
export class FxTable {
  private readonly series = new Map<Currency, RateSeries>();
  /** Memoised `currency|date` -> rate, because a table re-render asks for the same days. */
  private readonly rateCache = new Map<string, Decimal>();

  constructor(rows: readonly FxRate[]) {
    const grouped = new Map<Currency, Map<IsoDate, Decimal>>();

    for (const row of rows) {
      // Everything is stored EUR-based; anything else is a data bug we surface loudly.
      if (row.base !== FX_BASE_CURRENCY) {
        throw new Error(`fx_rates_daily must be ${FX_BASE_CURRENCY}-based, got base=${row.base}`);
      }
      let byDate = grouped.get(row.quote);
      if (!byDate) {
        byDate = new Map();
        grouped.set(row.quote, byDate);
      }
      byDate.set(row.date, toDecimal(row.rate));
    }

    for (const [quote, byDate] of grouped) {
      const dates = [...byDate.keys()].sort();
      this.series.set(quote, {
        dates,
        // Non-null assertion is safe: every date came out of this same map.
        rates: dates.map((date) => byDate.get(date)!),
      });
    }
  }

  /** Units of `currency` per 1 EUR on `date`, falling back to the last known weekday. */
  rateFromBase(currency: Currency, date: IsoDate): Decimal {
    if (currency === FX_BASE_CURRENCY) return ONE;

    const key = `${currency}|${date}`;
    const cached = this.rateCache.get(key);
    if (cached) return cached;

    const series = this.series.get(currency);
    if (!series || series.dates.length === 0) throw new FxRateUnavailableError(currency, date);

    const index = lastIndexAtOrBefore(series.dates, date);
    if (index < 0) throw new FxRateUnavailableError(currency, date);

    const rate = series.rates[index]!;
    this.rateCache.set(key, rate);
    return rate;
  }

  /**
   * The scalar rate between two currencies, for showing "1 EUR = 396 HUF".
   *
   * Do not multiply an amount by this: a precomputed quotient is rounded once
   * before it ever meets the amount, so converting and converting back drifts.
   * `convert` divides last, which round-trips exactly.
   */
  factor(from: Currency, to: Currency, date: IsoDate): Decimal {
    if (from === to) return ONE;
    return this.rateFromBase(to, date).dividedBy(this.rateFromBase(from, date));
  }

  convert(amount: Money, to: Currency, date: IsoDate): Money {
    if (amount.currency === to) return amount;

    const rateTo = this.rateFromBase(to, date);
    const rateFrom = this.rateFromBase(amount.currency, date);
    // Multiply first, divide once, at the end — 39 600 HUF -> EUR -> HUF is 39 600.
    return money(amount.amount.times(rateTo).dividedBy(rateFrom), to);
  }

  /** The newest date with any rate, i.e. what "current value" conversions use. */
  latestDate(): IsoDate | null {
    let latest: IsoDate | null = null;
    for (const series of this.series.values()) {
      const last = series.dates.at(-1);
      if (last && (latest === null || last > latest)) latest = last;
    }
    return latest;
  }

  currencies(): Currency[] {
    return [FX_BASE_CURRENCY, ...this.series.keys()];
  }

  get size(): number {
    let total = 0;
    for (const series of this.series.values()) total += series.dates.length;
    return total;
  }
}

/**
 * Index of the newest date `<= target`, or -1.
 *
 * ISO dates are lexicographically ordered, so plain string comparison is both
 * correct and faster than parsing. This is what makes weekends and holidays
 * resolve to Friday's ECB fixing without a calendar.
 */
export function lastIndexAtOrBefore(sortedDates: readonly IsoDate[], target: IsoDate): number {
  let low = 0;
  let high = sortedDates.length - 1;
  let result = -1;

  while (low <= high) {
    const mid = (low + high) >> 1;
    if (sortedDates[mid]! <= target) {
      result = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  return result;
}

export function buildFxTable(rows: readonly FxRate[]): FxTable {
  return new FxTable(rows);
}
